import React from 'react';

import { ImageLightboxProps } from '../types';

const EMPTY_IMAGE_LIGHTBOX: ImageLightboxProps = { thumbnails: [], parentElement: React.createRef() };

/**
 * Hook output type
 */
interface Output {
    /**
     * Open image slideshow lightbox callback function
     * @param parentElement required reference to the trigger element (ex: button) that open the lightbox. Needed to properly restore the focus on close
     * @param activeIndex   optioneal index of the image to show by default in the slideshow
     */
    openLightbox(parentElement: HTMLElement, activeIndex?: number): void;

    /**
     * Lightbox react node to add in the react tree
     */
    imageLightboxProps: ImageLightboxProps;
}

/**
 * A hook to setup the `ImageLightbox` and providing a callback to open it
 */
export const useImageLightbox = (thumbnails: ImageLightboxProps['thumbnails'] = []): Output => {
    const [imageLightboxProps, setImageLightboxProps] = React.useState<ImageLightboxProps>(EMPTY_IMAGE_LIGHTBOX);
    const onClose = React.useCallback(
        () => setImageLightboxProps((prevProps) => ({ ...prevProps, isOpen: false })),
        [],
    );

    // Callback to open the lightbox.
    const openLightbox = React.useCallback<Output['openLightbox']>(
        (newParentElement, activeIndex = 0) => {
            const hasThumbnails = !!thumbnails?.length;
            if (!hasThumbnails) {
                onClose();
                return;
            }

            // Open lightbox with setup props
            setImageLightboxProps({
                parentElement: { current: newParentElement },
                isOpen: hasThumbnails,
                onClose,
                thumbnails: thumbnails || [],
                slideshowActiveIndex: activeIndex,
            });
        },
        [onClose, thumbnails],
    );

    return { openLightbox, imageLightboxProps };
};
